import { useEffect, useState } from 'react';
import creditsafeLogo from '../../../../assets/images/Creditsafe-Logo-Red-4.png';

interface RiskBand {
  grade: string;
  intDesc: string;
  usDesc: string;
  min: number;
  max: number;
  color: string;
  bg: string;
  text: string;
  dot: string;
}

interface GaugeSVGProps {
  score: number;
  band: RiskBand;
}

interface CreditSafeScoreCardProps {
  score?: number;
  creditLimit?: string;
}

const RISK_BANDS: RiskBand[] = [
  {
    grade: 'A',
    intDesc: 'Very Low Risk',
    usDesc: 'Very Low Risk',
    min: 71,
    max: 100,
    color: '#1D9E75',
    bg: '#EAF3DE',
    text: '#3B6D11',
    dot: '#1D9E75',
  },
  {
    grade: 'B',
    intDesc: 'Low Risk',
    usDesc: 'Low Risk',
    min: 51,
    max: 70,
    color: '#5DCAA5',
    bg: '#E1F5EE',
    text: '#0F6E56',
    dot: '#5DCAA5',
  },
  {
    grade: 'C',
    intDesc: 'Moderate Risk',
    usDesc: 'Moderate Risk',
    min: 30,
    max: 50,
    color: '#EF9F27',
    bg: '#FAEEDA',
    text: '#854F0B',
    dot: '#EF9F27',
  },
  {
    grade: 'D',
    intDesc: 'High Risk',
    usDesc: 'High Risk',
    min: 21,
    max: 29,
    color: '#FAC775',
    bg: '#FFF8EC',
    text: '#BA7517',
    dot: '#FAC775',
  },
  {
    grade: 'E',
    intDesc: 'High Risk',
    usDesc: 'Very High Risk',
    min: 1,
    max: 20,
    color: '#E24B4A',
    bg: '#FCEBEB',
    text: '#A32D2D',
    dot: '#E24B4A',
  },
];

function getBand(score: number): RiskBand {
  return RISK_BANDS.find((b) => score >= b.min && score <= b.max) ?? RISK_BANDS[4];
}

function GaugeSVG({ score, band }: GaugeSVGProps) {
  const [animated, setAnimated] = useState<number>(0);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(score), 100);
    return () => clearTimeout(t);
  }, [score]);

  const R = 80;
  const cx = 110;
  const cy = 110;
  const startAngle = Math.PI;
  const totalArc = Math.PI;

  const arcPath = (start: number, end: number, r: number): string => {
    const s = { x: cx + r * Math.cos(start), y: cy + r * Math.sin(start) };
    const e = { x: cx + r * Math.cos(end), y: cy + r * Math.sin(end) };
    const large = end - start > Math.PI ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${large} 1 ${e.x} ${e.y}`;
  };

  const angle = startAngle + totalArc * (animated / 100);
  const knobX = cx + R * Math.cos(angle);
  const knobY = cy + R * Math.sin(angle);

  return (
    <svg viewBox="0 0 220 130" width="220" height="130">
      {/* Track */}
      <path
        d={arcPath(Math.PI, 2 * Math.PI, R)}
        fill="none"
        stroke="#E8E6DF"
        strokeWidth="14"
        strokeLinecap="round"
      />
      {/* Colored progress arc */}
      <path
        d={arcPath(Math.PI, Math.PI + totalArc * (animated / 100), R)}
        fill="none"
        stroke={band.color}
        strokeWidth="14"
        strokeLinecap="round"
        style={{ transition: 'all 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
      {/* Knob */}
      <circle
        cx={knobX}
        cy={knobY}
        r="9"
        fill="white"
        stroke={band.color}
        strokeWidth="3"
        style={{ transition: 'all 1.2s cubic-bezier(0.4,0,0.2,1)' }}
      />
      {/* Score number */}
      <text x={cx} y={cy - 4} textAnchor="middle" fontSize="36" fontWeight="600" fill={band.color}>
        {score}
      </text>
      {/* Sub-label */}
      <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#888780">
        out of 100
      </text>
    </svg>
  );
}

export default function CreditSafeScoreCard({
  score = 35,
  creditLimit = '$5,000.00',
}: CreditSafeScoreCardProps) {
  const band = getBand(score);

  const tableHeaders = [
    'International Score',
    'Int. Description',
    'US Description',
    'US Score Range',
  ];

  return (
    <div className="flex flex-col items-center justify-center bg-white rounded-lg border border-[#E8E6DF] w-full p-2 mb-4">
      {/* Score Panel */}
      <div className="w-full grid grid-cols-[1fr_1.4fr_1fr] items-center p-2.5 border-b border-[#EEECE6]">
        {/* Risk badge */}
        <div className="flex flex-col gap-1.5">
          <div className="w-full mb-2">
            <img src={creditsafeLogo} alt="Credit Safe" className="w-auto h-6 object-contain" />
          </div>
          <span className="text-[11px] text-[#888780] uppercase tracking-[0.08em]">Risk Level</span>
          <span
            className="inline-flex items-center gap-[7px] px-4 py-[7px] rounded-[30px] text-[13px] font-semibold w-fit border"
            style={{
              background: band.bg,
              color: band.text,
              borderColor: `${band.color}44`,
            }}>
            <span
              className="inline-block w-[9px] h-[9px] rounded-full shrink-0"
              style={{ background: band.color }}
            />
            {band.usDesc}
          </span>
          <span className="text-xs text-[#888780] mt-1">
            Grade: <strong style={{ color: band.color }}>{band.grade}</strong>
          </span>
        </div>

        {/* Gauge */}
        <div className="flex flex-col items-center">
          <span className="text-base text-[#888780] mb-0.5">Credit Score</span>
          <GaugeSVG score={score} band={band} />
        </div>

        {/* Credit limit */}
        <div className="text-right">
          <span className="block text-xs text-[#888780] uppercase tracking-[0.08em] mb-1">
            Recommended Limit
          </span>
          <span className="text-xl font-bold text-[#1A1A18] tracking-tight">{creditLimit}</span>
        </div>
      </div>

      {/* Risk Table */}
      <div className="p-2.5 w-full">
        <p className="text-xs text-[#888780] uppercase tracking-[0.08em] mb-3.5">
          Risk Scale Reference
        </p>
        <table className="w-full border-collapse text-[13px]">
          <thead>
            <tr>
              {tableHeaders.map((h) => (
                <th
                  key={h}
                  className="text-left text-[#888780] font-medium px-3 py-1.5 border-b border-[#EEECE6] text-xs">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {RISK_BANDS.map((b) => {
              const isActive = b.grade === band.grade;
              return (
                <tr
                  key={b.grade}
                  className="transition-colors duration-200"
                  style={{ background: isActive ? b.bg : 'transparent' }}>
                  {/* Grade + dot */}
                  <td className="px-3 py-[11px] border-b border-[#EEECE6]">
                    <span className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: b.dot }}
                      />
                      <strong
                        className={isActive ? 'font-bold' : 'font-medium'}
                        style={{ color: isActive ? b.color : '#1A1A18' }}>
                        {b.grade}
                      </strong>
                    </span>
                  </td>

                  {/* International description */}
                  <td className="px-3 py-[11px] border-b border-[#EEECE6] text-[#444441]">
                    {b.intDesc}
                  </td>

                  {/* US description badge */}
                  <td className="px-3 py-[11px] border-b border-[#EEECE6]">
                    <span
                      className="inline-block px-[11px] py-[3px] rounded-[20px] text-xs font-medium"
                      style={{ background: b.bg, color: b.text }}>
                      {b.usDesc}
                      {isActive && <span className="ml-[5px] text-[10px]">◀ current</span>}
                    </span>
                  </td>

                  {/* Score range */}
                  <td
                    className={`px-3 py-[11px] border-b border-[#EEECE6] tabular-nums ${
                      isActive ? 'font-bold' : 'font-normal'
                    }`}
                    style={{ color: isActive ? b.color : '#444441' }}>
                    {b.min} – {b.max}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
